/**
 * 答题分析 页面（近7天）
 */

import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useBrowserTitle } from 'hook/index';
import { BlockLoading, BlockEmpty } from 'component/BlockLoading/BlockLoading';
import { RightArrow } from 'component/Icon/index';
import { DialogConfirm } from 'component/Dialog/DialogConfirm';
import { statistic, StatisticCategory } from 'util/index';
import { getRecentCutoffDate, RECENT_RECORD_DAYS } from 'config/record';
import { getVideoTarget, buildVideoLink, VideoTarget } from 'page/lesson/List/videoMapping';
import { service } from 'service';
import styles from './RecordAnalysis.module.scss';

// 汇总接口返回的单日记录
interface SummaryDetail {
    totalNum: number;
    errorNum: number;
    date: string;
}
interface SummaryItem {
    appName: string;
    appNameChinese: string;
    detail: SummaryDetail[];
}

// 单个题型近7天聚合
interface SkillStat {
    app: string;
    appCh: string;
    // 练习题数
    practiced: number;
    // 错题数
    wrong: number;
    // 正确率
    rate: number;
}

interface State {
    loading: boolean;
    isVip: boolean;
    stats: SkillStat[];
}

// 小锁图标（内联 SVG）
function LockIcon({ size = 12, color = 'currentColor', className }: { size?: number; color?: string; className?: string }) {
    return (
        <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none"
             style={{verticalAlign: '-1px'}} aria-hidden="true">
            <rect x="5" y="11" width="14" height="9.5" rx="2" fill={color} />
            <path d="M8 11V7.8a4 4 0 0 1 8 0V11" stroke={color} strokeWidth="2" fill="none" />
        </svg>
    );
}

// 按正确率返回进度条与百分比文字配色
function getRateColors(rate: number): { bar: string; text: string } {
    if (rate >= 80) {
        return { bar: '#1D9E75', text: '#0F6E56' };
    }
    if (rate >= 60) {
        return { bar: '#EF9F27', text: '#854F0B' };
    }
    return { bar: '#E24B4A', text: '#A32D2D' };
}

// 题型彩色竖条配色，按题型名做简单散列，保证同一题型颜色固定（与答题记录页一致）
const SKILL_BAR_COLORS = ['#378ADD', '#1D9E75', '#9b59b6', '#e67e22', '#16a4c8'];
function getSkillBarColor(appName: string): string {
    let sum = 0;
    for (let i = 0; i < appName.length; i++) {
        sum += appName.charCodeAt(i);
    }
    return SKILL_BAR_COLORS[sum % SKILL_BAR_COLORS.length];
}

export default function RecordAnalysis() {
    useBrowserTitle('答题分析');
    const history = useHistory();
    const [needPayVisible, setNeedPayVisible] = useState(false);
    const [data, setData] = useState<State>({
        loading: true,
        isVip: false,
        stats: [],
    });

    useEffect(function(){
        service.get('/wx/record/summary')
            .then(function(out){
                if (out.status === 200 && out.data.status === 0) {
                    const cutoff = getRecentCutoffDate(RECENT_RECORD_DAYS);
                    const stats: SkillStat[] = (out.data.data || []).map(function(obj: SummaryItem){
                        let practiced = 0;
                        let wrong = 0;
                        obj.detail
                            .filter(function(d){ return d.date >= cutoff; })
                            .forEach(function(d){
                                practiced += d.totalNum;
                                wrong += d.errorNum;
                            });
                        const rate = practiced > 0 ? Math.round((practiced - wrong) / practiced * 100) : 0;
                        return { app: obj.appName, appCh: obj.appNameChinese, practiced, wrong, rate };
                    })
                    // 近7天没有练习的题型不展示
                    .filter(function(s: SkillStat){ return s.practiced > 0; })
                    // 错题多的排前面，其次正确率低的排前面
                    .sort(function(a: SkillStat, b: SkillStat){
                        if (b.wrong !== a.wrong) { return b.wrong - a.wrong; }
                        return a.rate - b.rate;
                    });
                    setData({ loading: false, isVip: !!out.data.isVip, stats });
                } else {
                    throw new Error('网络异常，请稍后再试');
                }
            })
            .catch(function(err: Error){
                setData({ loading: false, isVip: false, stats: [] });
                alert(err.message);
            });
    }, []);

    function goFailList(app: string) {
        history.push(`/mp/record/failList?appName=${encodeURIComponent(app)}`);
    }
    function requestVip(action: string) {
        statistic({ category: StatisticCategory.Record, action });
        setNeedPayVisible(true);
    }
    // 点击题型卡主体（含右侧「>」）：进入该题型近期做题详细记录（错题与正确的题都有，按时间排序）
    function goSkillDetail(stat: SkillStat) {
        history.push(`/mp/record/skillDailyRecord?appName=${encodeURIComponent(stat.app)}`);
    }
    // 「查看错题集」：与答题记录页一致——会员直接进入错题集，非会员弹出开通引导
    function onViewFailList(e: React.MouseEvent, stat: SkillStat) {
        e.stopPropagation();
        if (data.isVip) {
            goFailList(stat.app);
            return;
        }
        statistic({ category: StatisticCategory.Record, action: '点击-查看错题集-未开通' });
        setNeedPayVisible(true);
    }
    // 「视频讲解」：与答题记录页一致——跳转到对应题型的精选讲解视频
    function goVideo(e: React.MouseEvent, target: VideoTarget) {
        e.stopPropagation();
        statistic({ category: StatisticCategory.Record, action: '点击-技能视频讲解' });
        history.push(buildVideoLink(target));
    }
    // 非会员点击被锁的整行（进度条 / 正确率 / 行主体）：统一弹出开通会员引导，不进入详细记录
    function onLockedRowClick(e: React.MouseEvent) {
        e.stopPropagation();
        requestVip('点击-分析正确率-未开通');
    }
    function onFocusClick(stat: SkillStat) {
        if (!data.isVip) {
            requestVip('点击-重点关注练错题-未开通');
            return;
        }
        goFailList(stat.app);
    }
    function confirmOpenVip() {
        setNeedPayVisible(false);
        statistic({ category: StatisticCategory.Record, action: '点击-分析弹窗-开通会员' });
        history.push('/mp/user/pay');
    }

    if (data.loading) {
        return (<BlockLoading />);
    }
    if (data.stats.length < 1) {
        return (<BlockEmpty />);
    }

    // 近7天概览
    const totalPracticed = data.stats.reduce(function(sum, s){ return sum + s.practiced; }, 0);
    const totalWrong = data.stats.reduce(function(sum, s){ return sum + s.wrong; }, 0);
    const overallRate = totalPracticed > 0 ? Math.round((totalPracticed - totalWrong) / totalPracticed * 100) : 0;
    // 重点关注：错题最多（即排序后的第一个，且有错题）
    const focusStat = data.stats[0] && data.stats[0].wrong > 0 ? data.stats[0] : null;

    return (
        <div className={styles.page}>
            <div className={styles.overview}>
                <div className={styles.overviewHd}>近 {RECENT_RECORD_DAYS} 天概览</div>
                <div className={styles.overviewRow}>
                    <div className={styles.overviewCell}>
                        <div className={styles.overviewNum} style={{color: '#1a1a1a'}}>{totalPracticed}</div>
                        <div className={styles.overviewLab}>练习题数</div>
                    </div>
                    <div className={styles.overviewDiv}></div>
                    <div className={styles.overviewCell}>
                        <div className={styles.overviewNum} style={{color: '#e54d42'}}>{totalWrong}</div>
                        <div className={styles.overviewLab}>错题数</div>
                    </div>
                    <div className={styles.overviewDiv}></div>
                    <div className={styles.overviewCell}>
                        <div className={styles.overviewNum} style={{color: getRateColors(overallRate).text}}>{overallRate}%</div>
                        <div className={styles.overviewLab}>正确率</div>
                    </div>
                </div>
            </div>

            {
                focusStat && (
                    <div>
                        <div className={styles.sect}>重点关注</div>
                        <div className={styles.focus} onClick={onFocusClick.bind(null, focusStat)}>
                            <div className={styles.focusIcon}>!</div>
                            <div className={styles.focusBody}>
                                <div className={styles.focusTitle}>{focusStat.appCh}</div>
                                <div className={styles.focusSub}>近{RECENT_RECORD_DAYS}天错题最多 {focusStat.wrong} 题 · 正确率 {focusStat.rate}%</div>
                            </div>
                            <span className={styles.focusBtn}>
                                {!data.isVip && <LockIcon size={12} color="#fff" className={styles.lock} />}
                                练错题
                            </span>
                        </div>
                    </div>
                )
            }

            <div className={styles.sect}>题型分析（按错题数排序）</div>
            {
                data.stats.map(function(stat){
                    const colors = getRateColors(stat.rate);
                    // 非会员整行加锁：无论是否满分，进度条/正确率/行主体点击都引导开通
                    const locked = !data.isVip;
                    const videoTarget = getVideoTarget(stat.app);
                    return (
                        <div key={stat.app} className={styles.skillCard}>
                            <div className={styles.skillHead}>
                                <span className={styles.skillTitle}>
                                    <span className={styles.skillBar} style={{background: getSkillBarColor(stat.app)}} />
                                    {stat.appCh}
                                    {
                                        videoTarget && (
                                            <span onClick={(e) => goVideo(e, videoTarget)} className={styles.videoEntry}>
                                                <span className={styles.videoTri}></span>视频讲解
                                            </span>
                                        )
                                    }
                                </span>
                                <span onClick={(e) => onViewFailList(e, stat)} className={styles.errorEntry}>
                                    {!data.isVip && <LockIcon size={12} color="#e54d42" className={styles.errorEntryIcon} />}
                                    查看错题集 <RightArrow/>
                                </span>
                            </div>
                            <div className={styles.skillRow} onClick={locked ? onLockedRowClick : goSkillDetail.bind(null, stat)}>
                                <div className={styles.skillBody}>
                                    <div className={styles.progressLine}>
                                        <div className={styles.progressTrack}>
                                            <div className={styles.progressFill} style={{width: `${stat.rate}%`, background: colors.bar}} />
                                        </div>
                                        <span className={styles.skillWrongLine}>错 <span className={styles.skillWrongNum}>{stat.wrong}</span> / 练 {stat.practiced}</span>
                                    </div>
                                </div>
                                <span className={styles.rate} style={{color: colors.text}}>
                                    {locked && <LockIcon size={12} color="#b0b0b0" className={styles.lock} />}
                                    {stat.rate}%
                                </span>
                                <span className={styles.skillArrow}><RightArrow/></span>
                            </div>
                        </div>
                    );
                })
            }

            <DialogConfirm
                visible={needPayVisible}
                title="会员专享"
                okBtnText="开通会员"
                cancelBtnText="取消"
                onCancel={() => setNeedPayVisible(false)}
                onOk={confirmOpenVip}
                onMaskClick={() => setNeedPayVisible(false)}
            >
                错题本是会员专享功能，开通会员后可查看孩子的错题集、智能出题并免除广告。
            </DialogConfirm>
        </div>
    );
}
